import { applyBrandTheme } from '../lib/brandTheme.js';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import RavePilotHeader from '../components/RavePilotHeader.jsx';
import RavePilotFooter from '../components/RavePilotFooter.jsx';
import ScriptRecorder from '../components/ScriptRecorder.jsx';

// ── Interviewer record flow (/ask/:token) ─────────────────────────────────
// The brand shares this link with whoever plays the interviewer. Same
// camera-app experience customers get, but the teleprompter shows the
// question script and each take saves straight onto its question — no
// submission, no consent, no rewards. Re-recording replaces the video.

export default function Asker() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [stage, setStage] = useState('loading');   // loading | invalid | list | record
  const [error, setError] = useState(null);
  const [activeQ, setActiveQ] = useState(null);

  async function load() {
    try {
      const d = await api(`/api/asker/${token}`);
      setData(d);
      if (d?.collector?.brand) applyBrandTheme(d.collector.brand);
      setStage((s) => (s === 'loading' ? 'list' : s));
      return d;
    } catch (e) {
      setError(e.message || 'invalid link');
      setStage('invalid');
      return null;
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  // Poll while any take is still encoding so status pills flip on their own.
  useEffect(() => {
    if (stage !== 'list' || !data) return;
    const pending = (data.questions || []).some((q) => q.has_video && !q.stream_ready);
    if (!pending) return;
    const id = setInterval(() => { if (document.visibilityState !== 'hidden') load(); }, 4000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [stage, data]);

  if (stage === 'loading') {
    return <Shell brand={null}><div className="py-20 text-center text-ravepilot-muted">Loading…</div></Shell>;
  }
  if (stage === 'invalid') {
    return (
      <Shell brand={null}>
        <div className="py-16 text-center">
          <h1 className="vt-h-screen">This interviewer link isn't valid.</h1>
          <p className="vt-lead" style={{ marginTop: 10 }}>Ask your admin to send a fresh link from the dashboard. ({error})</p>
        </div>
      </Shell>
    );
  }

  const brand = data?.collector?.brand || {};
  const questions = data?.questions || [];
  const doneCount = questions.filter((q) => q.has_video).length;

  if (stage === 'record' && activeQ) {
    const line = activeQ.asker_script || activeQ.question_text;
    return (
      <ScriptRecorder
        brand={brand}
        script={{
          key: `ask:${activeQ.id}`,
          title: 'Ask the question',
          prompts: [{ label: '', template: line, blanks: [] }],
        }}
        invite_token={token}
        filledScript={{}}
        getUploadUrl={() => api(`/api/asker/${token}/questions/${activeQ.id}/upload-url`, { method: 'POST', body: {} })}
        onAccepted={async () => { await load(); setActiveQ(null); setStage('list'); }}
        onCancel={() => { setActiveQ(null); setStage('list'); }}
        maxDurationSeconds={60}
        clipNumber={questions.findIndex((q) => q.id === activeQ.id) + 1}
        totalClips={questions.length}
      />
    );
  }

  return (
    <Shell brand={brand}>
      <div className="animate-fade-in-up" style={{ marginBottom: 22 }}>
        <div className="vt-eyebrow"><span className="vt-bar"></span>Interviewer studio</div>
        <h1 className="vt-h-screen">Record the questions for {data.collector.name}.</h1>
        <p className="vt-lead">
          You're the interviewer. Tap a question, read it to the camera like you're asking a friend,
          and it goes live in the campaign the moment it finishes processing. Recording again replaces the old take.
        </p>
        <p className="vt-lead" style={{ marginTop: 6, fontWeight: 600 }}>
          {doneCount} of {questions.length} recorded
        </p>
      </div>

      <div className="vt-script-list">
        {questions.map((q, i) => (
          <button
            key={q.id}
            type="button"
            onClick={() => { setActiveQ(q); setStage('record'); }}
            className="vt-script-card vt-tap animate-fade-in-up"
            style={{ animationDelay: `${0.06 + i * 0.06}s` }}
          >
            {q.thumbnail_url ? (
              <span className="vt-script-icon" style={{ padding: 0, overflow: 'hidden' }}>
                <img src={q.thumbnail_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }} />
              </span>
            ) : (
              <span className="vt-script-icon" style={{ ['--vt-ico']: '#FFE7DC' }}>
                <span className="vt-script-glyph">🎬</span>
              </span>
            )}
            <div className="vt-script-body">
              <div className="vt-script-title">{q.question_text}</div>
              <div className="vt-script-summary">
                {q.stream_ready
                  ? `Recorded${q.duration_seconds ? ` · ${Math.round(q.duration_seconds)}s` : ''} · tap to re-record`
                  : q.has_video
                  ? 'Processing your take…'
                  : (q.asker_script ? 'Script ready · tap to record' : 'Tap to record')}
              </div>
            </div>
            <span className="vt-script-check" aria-hidden="true">
              {q.stream_ready ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 6 9 17l-5-5" />
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="9" opacity=".3" />
                  <polygon points="10 8 16 12 10 16" fill="currentColor" stroke="none" />
                </svg>
              )}
            </span>
          </button>
        ))}
      </div>
    </Shell>
  );
}

function Shell({ children, brand }) {
  return (
    <div className="vt-app min-h-screen flex flex-col">
      <RavePilotHeader brand={brand} />
      <main className="flex-1 w-full max-w-[760px] mx-auto px-4 md:px-6 py-6 md:py-10">{children}</main>
      <RavePilotFooter brand={brand} />
    </div>
  );
}
